import Markdown from 'react-markdown';
import { CodeBlock, theme } from '@agentisai/design-system';
import type { TurnArtifact } from '@agentisai/shared-types';

/**
 * A turn/ledger artifact enriched for display: `expired` marks a presigned
 * `url` past its ~1 h role-session lifetime (ADR-0040 ceiling) so the UI
 * shows an honest "ask the agent to re-share" state instead of a dead anchor.
 */
export type DisplayArtifact = TurnArtifact & { expired: boolean };

/** Human-readable artifact size — computed at render, never stored (R2). */
export function formatArtifactSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** CodeBlock language stamp for a preview's content type. */
function previewLanguage(contentType: string): string | undefined {
  if (contentType.includes('json')) return 'json';
  return undefined;
}

/**
 * ArtifactPreview — renders an artifact's inline preview by content type
 * (ADR-0040 / Stage 3c):
 *   - HTML → `iframe srcdoc` with `sandbox="allow-scripts"` so agent-built
 *     interactive HTML runs instead of rendering inert. `allow-same-origin`
 *     is deliberately withheld — the frame keeps an opaque origin (no
 *     cookies/storage/parent-DOM reach) and inherits the SPA's CSP; that
 *     pairing is the security posture for untrusted agent output;
 *   - markdown → rich render via react-markdown (safe by default — raw HTML
 *     in the source is NOT rendered; the Stage 3b PRD → spec document flow
 *     is the "real report" ADR-0040 deferred this for);
 *   - anything else text-family → DS CodeBlock.
 * Content only — callers own any disclosure/card chrome around it.
 */
export function ArtifactPreview({ artifact }: { artifact: TurnArtifact }) {
  if (!artifact.preview) return null;

  if (artifact.contentType.includes('html')) {
    return (
      <iframe
        sandbox="allow-scripts"
        srcDoc={artifact.preview}
        title={`Preview of ${artifact.filename}`}
        style={{
          width: '100%',
          height: 280,
          border: `1px solid ${theme.color.neutral.border}`,
          borderRadius: theme.radii.md,
          background: theme.color.neutral.bgSurface,
        }}
      />
    );
  }

  if (artifact.contentType.includes('markdown')) {
    return (
      <div
        style={{
          padding: `0 ${theme.space[2]}`,
          fontSize: theme.typography.fontSize.bodySm,
          lineHeight: theme.typography.lineHeight.docBody,
          color: theme.color.neutral.body,
          overflowWrap: 'break-word',
        }}
      >
        <Markdown>{artifact.preview}</Markdown>
      </div>
    );
  }

  return <CodeBlock code={artifact.preview} language={previewLanguage(artifact.contentType)} />;
}
ArtifactPreview.displayName = 'ArtifactPreview';
